import { api, unwrap, type ApiEnvelope } from "@/lib/api";

export type ShortlinkStatus = "active" | "paused" | "expired" | "archived";

export type ShortlinkKind =
	| "order_tracking"
	| "order_payment"
	| "order_invoice"
	| "monde_tracking"
	| "product"
	| "category"
	| "promotion"
	| "campaign"
	| "cms"
	| "custom";

export type ShortlinkChannel =
	| "sms"
	| "whatsapp"
	| "facebook"
	| "instagram"
	| "email"
	| "print"
	| "qr"
	| "ads"
	| "monitor"
	| "other";

export type ShortlinkSource = "system" | "manual";

export interface ShortlinkUtm {
	source?: string;
	medium?: string;
	campaign?: string;
	content?: string;
	term?: string;
}

export interface Shortlink {
	id: string;
	code: string;
	/** URL cliquable complète — calculée par l'API, jamais recomposée ici. */
	shortUrl: string;
	/** Forme SMS, sans protocole : 15 caractères au lieu de 23. */
	smsForm: string;
	/**
	 * `null` sur un lien système : la destination contient un jeton d'accès à la
	 * commande, et le masquage est appliqué CÔTÉ SERVEUR — l'interface ne fait
	 * que refléter une décision déjà prise.
	 */
	targetUrl: string | null;
	targetRedacted: boolean;
	fallbackUrl: string | null;
	kind: ShortlinkKind;
	source: ShortlinkSource;
	channel: ShortlinkChannel | null;
	campaign: string | null;
	label: string | null;
	utm: ShortlinkUtm;
	status: ShortlinkStatus;
	targetKind: string | null;
	targetRef: string | null;
	orderId: string | null;
	expiresAt: string | null;
	maxClicks: number | null;
	dispatchCount: number;
	firstDispatchedAt: string | null;
	lastDispatchedAt: string | null;
	clicksTotal: number;
	clicksHuman: number;
	clicksUnique: number;
	firstClickAt: string | null;
	lastClickAt: string | null;
	createdAt: string;
	updatedAt: string;
	archivedAt: string | null;
}

export interface ShortlinksPage {
	data: Shortlink[];
	total: number;
	page: number;
	pageSize: number;
}

export interface ShortlinksFilter {
	page?: number;
	pageSize?: number;
	q?: string;
	kind?: ShortlinkKind;
	source?: ShortlinkSource;
	channel?: ShortlinkChannel;
	campaign?: string;
	status?: ShortlinkStatus;
	sortBy?: "createdAt" | "clicksHuman" | "lastClickAt";
	sortDir?: "ASC" | "DESC";
}

export interface CreateShortlinkInput {
	targetUrl: string;
	kind: ShortlinkKind;
	fallbackUrl?: string;
	channel?: ShortlinkChannel;
	campaign?: string;
	label?: string;
	code?: string;
	utm?: ShortlinkUtm;
	expiresAt?: string;
	maxClicks?: number;
	targetKind?: string;
	targetRef?: string;
}

export interface UpdateShortlinkInput {
	targetUrl?: string;
	fallbackUrl?: string;
	label?: string;
	channel?: ShortlinkChannel;
	campaign?: string;
	status?: "active" | "paused";
	utm?: ShortlinkUtm;
	expiresAt?: string;
	maxClicks?: number;
	clearExpiresAt?: boolean;
	clearMaxClicks?: boolean;
	clearFallbackUrl?: boolean;
}

export interface SeriesPoint {
	date: string;
	humans: number;
	uniques: number;
	bots: number;
	prefetch: number;
	clicks: number;
}

export interface DimensionRow {
	value: string;
	clicks: number;
	humans: number;
}

export interface StatsTotals {
	clicks: number;
	humans: number;
	uniques: number;
	bots: number;
	prefetch: number;
	nonHumanRate: number;
	geoUnknownRate: number;
}

export interface LinkStats {
	series: SeriesPoint[];
	dimensions: Record<string, DimensionRow[]>;
	totals: StatsTotals;
	dispatch: { sent: number; clickedRate: number | null };
}

export interface StatsOverview {
	series: SeriesPoint[];
	dimensions: Record<string, DimensionRow[]>;
	totals: StatsTotals;
	activeLinks: number;
	topLinks: Array<{
		id: string;
		code: string;
		label: string | null;
		kind: string;
		humans: number;
		uniques: number;
		lastClickAt: string | null;
	}>;
}

export interface ShortlinkClick {
	id: string;
	occurredAt: string;
	isBot: boolean;
	isPrefetch: boolean;
	botReason: string | null;
	isUnique: boolean | null;
	countryCode: string | null;
	geoSource: string;
	browser: string | null;
	os: string | null;
	deviceType: string;
	deviceBrand: string | null;
	deviceModel: string | null;
	inApp: string | null;
	lang: string | null;
	referrerHost: string | null;
	channel: string | null;
	campaign: string | null;
}

export interface ClicksPage {
	data: ShortlinkClick[];
	total: number;
	page: number;
	pageSize: number;
}

export interface ShortlinkHealth {
	killed: boolean;
	degraded: boolean;
	queueDepth: number | null;
	cache: {
		hits: number;
		misses: number;
		staleServed: number;
		local: number;
		hitRate: number;
	};
	codes: { mints: number; collisions: number };
	jobs: {
		enabled: boolean;
		runs: number;
		lastRunAt: string | null;
		lastError: string | null;
		missSpike: number | null;
	};
}

const toQs = (filter: object): string => {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(filter)) {
		if (value === undefined || value === null || value === "") continue;
		params.set(key, String(value));
	}
	const qs = params.toString();
	return qs ? `?${qs}` : "";
};

export const shortLinksApi = {
	list: (filter: ShortlinksFilter = {}) =>
		unwrap<ShortlinksPage>(
			api.get<ApiEnvelope<ShortlinksPage>>(`/admin/shortlinks${toQs(filter)}`),
		),

	get: (id: string) =>
		unwrap<Shortlink>(api.get<ApiEnvelope<Shortlink>>(`/admin/shortlinks/${id}`)),

	create: (data: CreateShortlinkInput) =>
		unwrap<Shortlink>(
			api.post<ApiEnvelope<Shortlink>>("/admin/shortlinks", data),
		),

	update: (id: string, data: UpdateShortlinkInput) =>
		unwrap<Shortlink>(
			api.patch<ApiEnvelope<Shortlink>>(`/admin/shortlinks/${id}`, data),
		),

	archive: (id: string) => api.delete(`/admin/shortlinks/${id}`).then(() => undefined),

	invalidate: (id: string) =>
		api.post(`/admin/shortlinks/${id}/invalidate`).then(() => undefined),

	checkCode: (code: string) =>
		unwrap<{ available: boolean; code: string | null; reason?: string }>(
			api.get<ApiEnvelope<{ available: boolean; code: string | null; reason?: string }>>(
				`/admin/shortlinks/check${toQs({ code })}`,
			),
		),

	stats: (id: string, days = 30) =>
		unwrap<LinkStats>(
			api.get<ApiEnvelope<LinkStats>>(`/admin/shortlinks/${id}/stats${toQs({ days })}`),
		),

	overview: (days = 30) =>
		unwrap<StatsOverview>(
			api.get<ApiEnvelope<StatsOverview>>(`/admin/shortlinks/stats/overview${toQs({ days })}`),
		),

	clicks: (id: string, page = 1, pageSize = 50, includeBots = false) =>
		unwrap<ClicksPage>(
			api.get<ApiEnvelope<ClicksPage>>(
				`/admin/shortlinks/${id}/clicks${toQs({ page, pageSize, includeBots })}`,
			),
		),

	health: () =>
		unwrap<ShortlinkHealth>(
			api.get<ApiEnvelope<ShortlinkHealth>>("/admin/shortlinks/health"),
		),

	/** L'export passe par l'axios authentifié : le CSV n'est pas une route publique. */
	exportCsv: async (id: string, code: string): Promise<void> => {
		const res = await api.get<Blob>(`/admin/shortlinks/${id}/export.csv`, {
			responseType: "blob",
		});
		const url = URL.createObjectURL(res.data);
		const a = document.createElement("a");
		a.href = url;
		a.download = `clics-${code}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	},

	/** Le QR est produit par l'API pour qu'un code imprimé soit identique partout. */
	qrUrl: (id: string, size = 512) => `/admin/shortlinks/${id}/qr.png${toQs({ size })}`,

	qrBlob: async (id: string, size = 512): Promise<string> => {
		const res = await api.get<Blob>(`/admin/shortlinks/${id}/qr.png${toQs({ size })}`, {
			responseType: "blob",
		});
		return URL.createObjectURL(res.data);
	},
};

/** Libellés français des types de liens. */
export const KIND_LABEL: Record<ShortlinkKind, string> = {
	order_tracking: "Suivi de commande",
	order_payment: "Paiement",
	order_invoice: "Facture",
	monde_tracking: "Suivi Monde",
	product: "Produit",
	category: "Catégorie",
	promotion: "Promotion",
	campaign: "Campagne",
	cms: "Page CMS",
	custom: "Autre",
};

export const CHANNEL_LABEL: Record<ShortlinkChannel, string> = {
	sms: "SMS",
	whatsapp: "WhatsApp",
	facebook: "Facebook",
	instagram: "Instagram",
	email: "E-mail",
	print: "Imprimé",
	qr: "QR code",
	ads: "Publicité",
	monitor: "Supervision",
	other: "Autre",
};

export const STATUS_LABEL: Record<ShortlinkStatus, string> = {
	active: "Actif",
	paused: "En pause",
	expired: "Expiré",
	archived: "Archivé",
};
